import { DateTime } from "@bps/date-time";
import {
  FontSizes,
  FontWeights,
  NativeList,
  noWrap,
  Stack,
  StackItem,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { useStores } from "@stores/hooks/useStores.ts";

import { ConfidentialByProviderText } from "../investigations/side-panel/ConfidentialByProviderText.tsx";
import {
  EN_DASH,
  formatDateStringToTime,
  isBloodPressureOrPulse,
  ObservationItemProps,
  ObservationsLabels
} from "./utils.ts";

export const GroupByDateObservationsView: React.FC<ObservationItemProps> = ({
  primaryKey,
  observations
}) => {
  const theme = useTheme();
  const { core } = useStores();
  const hasMultipleObservationsSameDay = observations.some(
    (observation, index) =>
      index > 0 &&
      DateTime.fromISO(observation.timestamp).hasSame(
        DateTime.fromISO(observations[index - 1].timestamp),
        "day"
      )
  );

  return (
    <Stack
      styles={{
        root: {
          paddingLeft: 32
        }
      }}
      key={primaryKey}
    >
      {(isBloodPressureOrPulse(primaryKey) ||
        hasMultipleObservationsSameDay) && (
        <Stack horizontal verticalAlign="center">
          <Text
            styles={{
              root: {
                fontSize: FontSizes.size12,
                color: theme.palette.neutralSecondary
              }
            }}
          >
            {primaryKey}
          </Text>
        </Stack>
      )}

      {isBloodPressureOrPulse(primaryKey) ? (
        <NativeList>
          {observations.map(observation => {
            let displayText = "";

            const labelText = observation.label
              ? ` ${EN_DASH} ${observation.label}`
              : "";

            switch (observation.typeLabel) {
              case ObservationsLabels.BloodPressure:
                displayText =
                  observation.systolic && observation.diastolic
                    ? `${observation.systolic}/${observation.diastolic}`
                    : observation.value;
                break;

              case ObservationsLabels.PulseBpmInTreeView:
                displayText = observation.pulse
                  ? `${observation.pulse} bpm`
                  : `${observation.value} bpm`;
                break;

              default:
                displayText = observation.value;
                break;
            }

            return (
              <li key={observation.id}>
                <Stack
                  horizontal
                  verticalAlign="center"
                  tokens={{ childrenGap: 4 }}
                >
                  <Text>
                    {formatDateStringToTime(observation.timestamp)} {EN_DASH}{" "}
                  </Text>
                  <Text
                    styles={{
                      root: {
                        fontWeight: FontWeights.semibold
                      }
                    }}
                  >
                    {displayText}
                  </Text>
                  <Text>{labelText}</Text>
                </Stack>
              </li>
            );
          })}
        </NativeList>
      ) : (
        <Stack>
          {observations.length === 1 ? (
            <Stack
              tokens={{ childrenGap: 4 }}
              horizontal
              verticalAlign="center"
            >
              <Text
                styles={{
                  root: {
                    fontSize: FontSizes.size12,
                    color: theme.palette.neutralSecondary,
                    flex: 1
                  }
                }}
              >
                {observations[0].typeLabel ===
                ObservationsLabels.TemperatureInTreeView
                  ? ObservationsLabels.Temperature
                  : observations[0].typeLabel}
              </Text>
              {core.hasAccessToSecGroup(observations[0].secGroupId) ? (
                <Text
                  styles={{
                    root: {
                      color: theme.palette.neutralPrimary,
                      fontWeight: FontWeights.semibold,
                      flex: 1
                    }
                  }}
                >
                  {observations[0].value}
                </Text>
              ) : (
                <StackItem styles={{ root: { flex: 1 } }}>
                  <ConfidentialByProviderText
                    secGroupId={observations[0].secGroupId ?? ""}
                    showIcon
                    styles={{
                      root: {
                        ...noWrap,
                        flex: 1
                      }
                    }}
                  />
                </StackItem>
              )}
            </Stack>
          ) : (
            observations.map(observation => {
              return (
                <Stack key={observation.id} tokens={{ childrenGap: 4 }}>
                  {formatDateStringToTime(observation.timestamp)}
                  {EN_DASH}
                  {core.hasAccessToSecGroup(observations[0].secGroupId) ? (
                    observation.value
                  ) : (
                    <ConfidentialByProviderText
                      secGroupId={observations[0].secGroupId ?? ""}
                      showIcon
                      styles={{
                        root: {
                          ...noWrap
                        }
                      }}
                    />
                  )}
                </Stack>
              );
            })
          )}
        </Stack>
      )}
    </Stack>
  );
};
